import React, { useEffect, useId, useRef, useState } from 'react';
import { cn } from '../poker-ui/utils/cn';
import { markLobbyModeSwitchPlayTilesOnce } from './lobbyHubShared';
import { POKER_LOBBY_TEMP_DISABLED } from '../lib/pokerLobbyStub';

function LobbySwitcherSnakeRing({ gradientId, c1, c2, pulse }) {
  return (
    <svg
      className="pointer-events-none absolute inset-0 z-[3] h-full w-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={c1} />
          <stop offset="50%" stopColor={c2} />
          <stop offset="100%" stopColor={c1} />
        </linearGradient>
      </defs>
      <rect
        x="2"
        y="2"
        width="96"
        height="96"
        rx="13"
        ry="13"
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="2.5"
        strokeLinecap="round"
        pathLength="100"
        strokeDasharray="22 78"
        vectorEffect="nonScalingStroke"
        className={cn('lobby-switcher-snake-stroke', pulse && 'lobby-switcher-snake-stroke--pulse')}
      />
    </svg>
  );
}

/**
 * Переключатель «Сека | Покер» в духе мобильных хабов (Miniclip): сегмент, скользящая подсветка.
 */
export function LobbyGameSwitcher({
  active,
  onSelectSeka,
  onSelectPoker,
  onSelectBelot,
  className,
  /** Короткая подпись у вкладки «Покер» (например «Превью») */
  pokerHint,
}) {
  const [sekaImgMissing, setSekaImgMissing] = useState(false);
  const [pokerImgMissing, setPokerImgMissing] = useState(false);
  const [belotImgMissing, setBelotImgMissing] = useState(false);
  const prevActiveRef = useRef(active);
  const [pulseTarget, setPulseTarget] = useState(null);
  const [pulseKey, setPulseKey] = useState(0);
  const snakeUid = useId().replace(/:/g, '');

  useEffect(() => {
    if (prevActiveRef.current !== active) {
      setPulseTarget(active);
      setPulseKey((v) => v + 1);
      prevActiveRef.current = active;
      return;
    }
    prevActiveRef.current = active;
  }, [active]);

  return (
    <div className={cn('mb-3 w-full', className)} role="tablist" aria-label="Выбор игры">
      <div
        className={cn(
          'relative grid grid-cols-3 gap-1 rounded-2xl',
          /* minmax(0,1fr) через min-w-0 у детей — иначе широкие img ломают сетку и обрезаются на узких экранах */
          'bg-gradient-to-b from-[#0c1624] to-[#060b12]',
          'p-1 shadow-[inset_0_2px_10px_rgba(0,0,0,0.45),0_6px_20px_rgba(0,0,0,0.35)]'
        )}
      >
        <button
          type="button"
          role="tab"
          aria-label="Сека"
          aria-selected={active === 'seka'}
          tabIndex={0}
          onClick={() => {
            if (active !== 'seka') {
              markLobbyModeSwitchPlayTilesOnce();
              onSelectSeka?.();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              if (POKER_LOBBY_TEMP_DISABLED) onSelectBelot?.();
              else onSelectPoker?.();
            }
          }}
          className={cn(
            'lobby-focus relative z-[1] flex min-h-0 min-w-0 items-center justify-center rounded-[13px] border-0 bg-transparent px-0.5 py-0 shadow-none outline-none ring-0 ring-offset-0',
            'h-[52px] sm:h-[54px]',
            'transition-all duration-200 ease-out',
            active === 'seka'
              ? 'scale-[1.04] shadow-[0_0_14px_rgba(232,197,71,0.14)]'
              : 'opacity-[0.58] brightness-[0.68] saturate-[0.72] hover:opacity-[0.78] hover:brightness-[0.88] hover:saturate-[0.9] active:opacity-90 active:brightness-95 active:saturate-100'
          )}
        >
          {!sekaImgMissing ? (
            <img
              src="/logotip-seka-button.png"
              alt=""
              onError={() => setSekaImgMissing(true)}
              className={cn(
                'relative z-[1] mx-auto block h-auto max-h-[46px] w-full max-w-[min(107px,100%)] origin-center object-contain object-center transition-transform duration-200 ease-out sm:max-h-[48px]',
                active === 'seka' ? 'scale-[1.22]' : 'scale-[1.08]'
              )}
              aria-hidden
            />
          ) : (
            <span
              className={cn(
                'relative z-[1] origin-center px-3 text-sm font-bold uppercase tracking-wide transition-transform duration-200 ease-out',
                active === 'seka' && 'scale-100'
              )}
            >
              Сека
            </span>
          )}
          {active === 'seka' ? (
            <LobbySwitcherSnakeRing
              key={`snake-seka-${pulseKey}`}
              gradientId={`snake-seka-${snakeUid}`}
              c1="#e8c547"
              c2="#fff4c8"
              pulse={pulseTarget === 'seka'}
            />
          ) : null}
        </button>
        <button
          type="button"
          role="tab"
          aria-label={POKER_LOBBY_TEMP_DISABLED ? 'Покер (в разработке)' : 'Покер'}
          aria-selected={!POKER_LOBBY_TEMP_DISABLED && active === 'poker'}
          tabIndex={POKER_LOBBY_TEMP_DISABLED ? -1 : 0}
          disabled={POKER_LOBBY_TEMP_DISABLED}
          onClick={() => {
            if (POKER_LOBBY_TEMP_DISABLED) return;
            if (active !== 'poker') {
              markLobbyModeSwitchPlayTilesOnce();
              onSelectPoker?.();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              onSelectSeka?.();
            }
            if (POKER_LOBBY_TEMP_DISABLED && e.key === 'ArrowRight') {
              e.preventDefault();
              onSelectBelot?.();
            }
          }}
          className={cn(
            'lobby-focus relative z-[1] flex min-h-0 min-w-0 items-center justify-center rounded-[13px] border-0 bg-transparent px-0.5 py-0 shadow-none outline-none ring-0 ring-offset-0',
            'h-[52px] sm:h-[54px]',
            'transition-all duration-200 ease-out',
            POKER_LOBBY_TEMP_DISABLED &&
              'cursor-not-allowed opacity-[0.42] saturate-[0.55] brightness-[0.75] hover:opacity-[0.42] hover:brightness-[0.75] hover:saturate-[0.55]',
            !POKER_LOBBY_TEMP_DISABLED &&
              active === 'poker' &&
              'scale-[1.04] shadow-[0_0_14px_rgba(52,211,153,0.14)]',
            !POKER_LOBBY_TEMP_DISABLED &&
              active !== 'poker' &&
              'opacity-[0.58] brightness-[0.68] saturate-[0.72] hover:opacity-[0.78] hover:brightness-[0.88] hover:saturate-[0.9] active:opacity-90 active:brightness-95 active:saturate-100'
          )}
        >
          {!pokerImgMissing ? (
            <img
              src="/logotip-poker-button.png"
              alt=""
              onError={() => setPokerImgMissing(true)}
              className={cn(
                'relative z-[1] mx-auto block h-auto max-h-[46px] w-full max-w-[min(107px,100%)] origin-center object-contain object-center transition-transform duration-200 ease-out sm:max-h-[48px]',
                !POKER_LOBBY_TEMP_DISABLED && active === 'poker' && 'scale-100'
              )}
              aria-hidden
            />
          ) : (
            <span
              className={cn(
                'relative z-[1] origin-center px-3 text-sm font-bold uppercase tracking-wide transition-transform duration-200 ease-out',
                !POKER_LOBBY_TEMP_DISABLED && active === 'poker' && 'scale-100'
              )}
            >
              Покер {pokerHint ? `· ${pokerHint}` : ''}
            </span>
          )}
          {POKER_LOBBY_TEMP_DISABLED ? (
            <span
              className="pointer-events-none absolute bottom-1 left-1/2 z-[4] max-w-[calc(100%-4px)] -translate-x-1/2 truncate rounded bg-black/75 px-1 py-px text-[7px] font-extrabold uppercase leading-tight tracking-wide text-amber-200 ring-1 ring-amber-500/35 sm:text-[8px]"
              aria-hidden
            >
              В разработке
            </span>
          ) : active === 'poker' ? (
            <LobbySwitcherSnakeRing
              key={`snake-poker-${pulseKey}`}
              gradientId={`snake-poker-${snakeUid}`}
              c1="#34d399"
              c2="#a7f3d0"
              pulse={pulseTarget === 'poker'}
            />
          ) : null}
        </button>
        <button
          type="button"
          role="tab"
          aria-label="Белот"
          aria-selected={active === 'belot'}
          tabIndex={0}
          onClick={() => {
            if (active !== 'belot') {
              markLobbyModeSwitchPlayTilesOnce();
              onSelectBelot?.();
            }
          }}
          onKeyDown={(e) => {
            if (POKER_LOBBY_TEMP_DISABLED && e.key === 'ArrowLeft') {
              e.preventDefault();
              onSelectSeka?.();
            }
          }}
          className={cn(
            'lobby-focus relative z-[1] flex min-h-0 min-w-0 items-center justify-center rounded-[13px] border-0 bg-transparent px-0.5 py-0 shadow-none outline-none ring-0 ring-offset-0',
            'h-[52px] sm:h-[54px]',
            'transition-all duration-200 ease-out',
            active === 'belot'
              ? 'scale-[1.04] shadow-[0_0_14px_rgba(129,140,248,0.16)]'
              : 'opacity-[0.58] brightness-[0.68] saturate-[0.72] hover:opacity-[0.78] hover:brightness-[0.88] hover:saturate-[0.9] active:opacity-90 active:brightness-95 active:saturate-100'
          )}
        >
          {!belotImgMissing ? (
            <img
              src="/belot-button.png"
              alt=""
              onError={() => setBelotImgMissing(true)}
              className={cn(
                'relative z-[1] mx-auto block h-auto max-h-[46px] w-full max-w-[min(107px,100%)] origin-center object-contain object-center transition-transform duration-200 ease-out sm:max-h-[48px]',
                active === 'belot' && 'scale-100'
              )}
              aria-hidden
            />
          ) : (
            <span
              className={cn(
                'relative z-[1] origin-center px-2 text-sm font-bold uppercase tracking-wide transition-transform duration-200 ease-out',
                active === 'belot' && 'scale-100'
              )}
            >
              Белот
            </span>
          )}
          {active === 'belot' ? (
            <LobbySwitcherSnakeRing
              key={`snake-belot-${pulseKey}`}
              gradientId={`snake-belot-${snakeUid}`}
              c1="#818cf8"
              c2="#e0e7ff"
              pulse={pulseTarget === 'belot'}
            />
          ) : null}
        </button>
      </div>
    </div>
  );
}
